import React from 'react';
import {
  OpsFilterModel,
  OpsLostAgentModel,
  OpsLostAnalysisModel,
  OpsPendingPipelineModel
} from '../../../../shared/models/lead-ops.model';
import {
  getOpsLostAnalysis,
  getOpsPendingPipeline
} from '../../../../shared/services/data/lead-ops.data';
import { VERTICAL_STYLE, styleFor } from '../../../shared/lead-visuals';
import { useOpsReport } from '../components/use-ops-report.hook';
import { OpsBars, OpsSection, OpsStat, TD, TH } from '../components/ops-section.component';

const titleCase = (value: string): string =>
  (value || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const verticalTone = (key: string) => styleFor(VERTICAL_STYLE, key).split(' ')[0];

export const PendingPipelineSection: React.FC<{ filter: OpsFilterModel }> = ({ filter }) => {
  const { data, loading, error, reload } = useOpsReport<OpsPendingPipelineModel>(
    getOpsPendingPipeline,
    filter
  );

  return (
    <OpsSection
      title="Pending pipeline"
      subtitle="Leads waiting on something. Aging is measured from the last update, not from creation — a lead touched yesterday is not stale however old it is."
      accent="bg-creams-600"
      loading={loading}
      error={error}
      empty={!!data && data.total === 0}
      onRetry={reload}
    >
      {data ? (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <OpsStat label="Pending" value={data.total} />
            <OpsStat
              label="Over a week"
              value={data.aging
                .filter((row) => ['7-14d', '14-30d', '30d+'].includes(row.key))
                .reduce((sum, row) => sum + row.count, 0)}
              tone="bad"
              hint="Waiting long enough to need a nudge"
            />
            <OpsStat
              label="Reasons"
              value={data.byReason.length}
              hint={
                data.byReason[0] ? `Most common: ${titleCase(data.byReason[0].key)}` : undefined
              }
            />
            <OpsStat label="Verticals" value={data.byVertical.length} />
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <div>
              <h3 className="mb-2 font-gilroy-medium text-sm text-neutral-400">Waiting on</h3>
              <OpsBars rows={data.byReason} label={titleCase} />
            </div>
            <div>
              <h3 className="mb-2 font-gilroy-medium text-sm text-neutral-400">Age</h3>
              <OpsBars rows={data.aging} tone={() => 'bg-creams-700'} />
            </div>
            <div>
              <h3 className="mb-2 font-gilroy-medium text-sm text-neutral-400">By vertical</h3>
              <OpsBars rows={data.byVertical} label={titleCase} tone={verticalTone} />
            </div>
            <div>
              <h3 className="mb-2 font-gilroy-medium text-sm text-neutral-400">By value tier</h3>
              <OpsBars rows={data.byValueTier} label={titleCase} />
            </div>
          </div>
        </div>
      ) : null}
    </OpsSection>
  );
};

export const LostAnalysisSection: React.FC<{ filter: OpsFilterModel }> = ({ filter }) => {
  const { data, loading, error, reload } = useOpsReport<OpsLostAnalysisModel>(
    getOpsLostAnalysis,
    filter
  );

  return (
    <OpsSection
      title="Lost analysis"
      subtitle="Not interested and not qualified. Rates are against resolved leads, and agents below the volume threshold are left out rather than ranked on noise."
      accent="bg-secondary-500"
      loading={loading}
      error={error}
      empty={!!data && data.totals.lost === 0}
      onRetry={reload}
    >
      {data ? (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <OpsStat label="Lost" value={data.totals.lost} tone="bad" />
            <OpsStat
              label="Loss rate"
              value={`${data.totals.lostRate}%`}
              tone="bad"
              hint={`of ${data.totals.resolvedTotal} resolved`}
            />
            <OpsStat label="Not interested" value={data.totals.notInterested} />
            <OpsStat label="Not qualified" value={data.totals.notQualified} />
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <div>
              <h3 className="mb-2 font-gilroy-medium text-sm text-neutral-400">Reasons given</h3>
              <OpsBars rows={data.byReason} label={titleCase} tone={() => 'bg-secondary-400'} />
            </div>
            <div>
              <h3 className="mb-2 font-gilroy-medium text-sm text-neutral-400">By channel</h3>
              <OpsBars rows={data.byChannel} label={titleCase} />
            </div>
          </div>

          {data.byAgent.length ? (
            <div>
              <h3 className="mb-1 font-gilroy-medium text-sm text-neutral-400">By agent</h3>
              <p className="mb-2 text-xs text-neutral-300">
                Only agents with at least {data.minResolvedThreshold} resolved leads. Peer median{' '}
                {data.peerMedianRate}% — a rate only means something next to its peers.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-sm">
                  <thead>
                    <tr className="bg-creams-200">
                      {['Agent', 'Resolved', 'Lost', 'Loss rate', 'vs peers'].map((c: string) => (
                        <th key={c} className={TH}>
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.byAgent.map((row: OpsLostAgentModel) => {
                      const delta = Math.round((row.lostRate - data.peerMedianRate) * 10) / 10;
                      return (
                        <tr key={row.userId} className="hover:bg-creams-100">
                          <td className={`${TD} font-gilroy-medium text-neutral-500`}>
                            {row.userIdUser?.name ?? row.userId}
                          </td>
                          <td className={`${TD} text-neutral-400`}>{row.resolved}</td>
                          <td className={`${TD} text-neutral-400`}>{row.lost}</td>
                          <td className={`${TD} text-secondary-600`}>{row.lostRate}%</td>
                          <td className={TD}>
                            <span className={delta > 0 ? 'text-secondary-600' : 'text-primary-600'}>
                              {delta > 0 ? `+${delta}` : delta}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </OpsSection>
  );
};
